import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const InputGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(
					"flex w-full flex-col rounded-lg border border-input bg-card shadow-md transition-all duration-200 focus-within:border-accent-blue-border focus-within:shadow-lg",
					className
				)}
				{...props}
			/>
		);
	}
);
InputGroup.displayName = "InputGroup";

interface InputGroupAddonProps extends React.HTMLAttributes<HTMLDivElement> {
	align?: "start" | "end" | "block-start" | "block-end";
}

const InputGroupAddon = React.forwardRef<HTMLDivElement, InputGroupAddonProps>(
	({ className, align = "end", ...props }, ref) => {
		const alignmentClasses = {
			start: "items-start",
			end: "items-end",
			"block-start": "items-start",
			"block-end": "items-end",
		};

		return (
			<div
				ref={ref}
				className={cn(
					"inline-flex gap-2 px-3 py-2 bg-muted/30 dark:bg-input/50",
					alignmentClasses[align],
					className
				)}
				{...props}
			/>
		);
	}
);
InputGroupAddon.displayName = "InputGroupAddon";

const InputGroupText = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
	({ className, ...props }, ref) => {
		return <span ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />;
	}
);
InputGroupText.displayName = "InputGroupText";

const InputGroupButton = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
	({ className, ...props }, ref) => {
		return <Button ref={ref} className={cn("", className)} {...props} />;
	}
);
InputGroupButton.displayName = "InputGroupButton";

const InputGroupTextarea = React.forwardRef<
	HTMLTextAreaElement,
	React.ComponentProps<typeof Textarea>
>(({ className, ...props }, ref) => {
	return (
		<Textarea
			ref={ref}
			disableFocusStyle
			className={cn(
				"flex-1 resize-none border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-input/40",
				className
			)}
			{...props}
		/>
	);
});
InputGroupTextarea.displayName = "InputGroupTextarea";

const InputGroupInput = React.forwardRef<HTMLInputElement, React.ComponentProps<typeof Input>>(
	({ className, ...props }, ref) => {
		return (
			<Input
				ref={ref}
				disableFocusStyle
				className={cn(
					"flex-1 border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-input/40",
					className
				)}
				{...props}
			/>
		);
	}
);
InputGroupInput.displayName = "InputGroupInput";

export {
	InputGroup,
	InputGroupAddon,
	InputGroupText,
	InputGroupButton,
	InputGroupTextarea,
	InputGroupInput,
};
